// Ultimate Card Game (UCG) - Player Taunts & Emotes Definitions
//
// Defines in-match commander taunts accessible by clicking the player's
// avatar portrait in the top HUD. Architected for future customization
// via the Cyber Slot Machine and Cyber Hangar & Showroom.

import { getIconSvg } from '../ui/icons.js';

export const TAUNT_DEFINITIONS = Object.freeze({
  hello: {
    id: 'hello',
    label: 'Hello',
    text: 'Hello',
    icon: getIconSvg('taunt_wave', 16),
    accentIcon: getIconSvg('taunt_comms', 14),
    callout: 'TACTICAL COMMS',
    rarity: 'common',
    pitch: 900,
    themeColor: '#00e5ff',
    glowColor: 'rgba(0, 229, 255, 0.85)',
    sparkGlyphs: ['✦', '★', '▲', '◆']
  },
  gotchya: {
    id: 'gotchya',
    label: 'I gotch ya',
    text: 'I gotch ya',
    icon: getIconSvg('target', 16),
    accentIcon: getIconSvg('energy', 14),
    callout: 'CRITICAL LOCK',
    rarity: 'common',
    pitch: 1350,
    themeColor: '#ffcc00',
    glowColor: 'rgba(255, 204, 0, 0.9)',
    sparkGlyphs: ['✦', '★', '▲', '◆']
  },
  revenge: {
    id: 'revenge',
    label: 'Revenge',
    text: 'Revenge',
    icon: getIconSvg('swords', 16),
    accentIcon: getIconSvg('kamikaze', 14),
    callout: 'COUNTER STRIKE',
    rarity: 'common',
    pitch: 580,
    themeColor: '#ff2255',
    glowColor: 'rgba(255, 34, 85, 0.9)',
    sparkGlyphs: ['✦', '★', '▲', '◆']
  },
  // Lockable taunts (see LOCKABLE_TAUNT_IDS below) - unlocked one at a time via
  // the Cyber Slot Machine's 'taunt' Reel 2 category (js/engine/slotMachine.js),
  // never owned by default. BotAI always has all of these available regardless
  // of unlock state (see ALL_TAUNT_IDS + js/engine/ai.js) - only a human
  // player's equip loadout is gated behind unlocking + the 3-slot cap.
  gg: {
    id: 'gg',
    label: 'Good Game',
    text: 'Good Game!',
    icon: getIconSvg('taunt_handshake', 16),
    accentIcon: getIconSvg('trophy', 14),
    callout: 'COMMS OPEN',
    rarity: 'rare',
    pitch: 700,
    themeColor: '#00ffa8',
    glowColor: 'rgba(0, 255, 168, 0.85)',
    sparkGlyphs: ['✦', '★', '▲', '◆']
  },
  miscalc: {
    id: 'miscalc',
    label: 'Miscalculated',
    text: 'Malfunction!',
    icon: getIconSvg('kamikaze', 16),
    accentIcon: getIconSvg('taunt_wrench', 14),
    callout: 'SELF-DIAGNOSTIC',
    rarity: 'rare',
    pitch: 420,
    themeColor: '#ff8800',
    glowColor: 'rgba(255, 136, 0, 0.85)',
    sparkGlyphs: ['✦', '★', '▲', '◆']
  },
  outmaneuvered: {
    id: 'outmaneuvered',
    label: 'Outmaneuvered',
    text: 'Outflanked!',
    icon: getIconSvg('taunt_satellite', 16),
    accentIcon: getIconSvg('target', 14),
    callout: 'TACTICAL SUPREMACY',
    rarity: 'rare',
    pitch: 1100,
    themeColor: '#2979ff',
    glowColor: 'rgba(41, 121, 255, 0.85)',
    sparkGlyphs: ['✦', '★', '▲', '◆']
  },
  overdrive: {
    id: 'overdrive',
    label: 'Overdrive',
    text: 'WEAPONS HOT!',
    icon: getIconSvg('energy', 16),
    accentIcon: getIconSvg('flame', 14),
    callout: 'REACTOR CRITICAL',
    rarity: 'epic',
    pitch: 1600,
    themeColor: '#b026ff',
    glowColor: 'rgba(176, 38, 255, 0.9)',
    sparkGlyphs: ['✦', '★', '▲', '◆']
  },
  standdown: {
    id: 'standdown',
    label: 'Stand Down',
    text: 'Final warning.',
    icon: getIconSvg('warning', 16),
    accentIcon: getIconSvg('taunt_radiation', 14),
    callout: 'THREAT LEVEL: MAX',
    rarity: 'epic',
    pitch: 300,
    themeColor: '#d10000',
    glowColor: 'rgba(209, 0, 0, 0.9)',
    sparkGlyphs: ['✦', '★', '▲', '◆']
  }
});

export const DEFAULT_EQUIPPED_TAUNT_IDS = Object.freeze(['hello', 'gotchya', 'revenge']);

// The 5 taunts a human player must unlock via the Slot Machine - excludes the
// 3 starter taunts above, which every account already owns from the start.
export const LOCKABLE_TAUNT_IDS = Object.freeze(['gg', 'miscalc', 'outmaneuvered', 'overdrive', 'standdown']);

// A human player's in-match quick-select loadout is capped at this many
// equipped taunts (js/engine/inventory.js setEquippedTaunts / equipTaunt);
// BotAI is exempt, see ALL_TAUNT_IDS.
export const MAX_EQUIPPED_TAUNTS = 3;

// Every taunt ID that exists, unlocked or not. BotAI draws from this full set
// (js/engine/ai.js) rather than DEFAULT_EQUIPPED_TAUNT_IDS or a player's own
// unlock/equip state - the bot has no inventory and isn't gated by the
// human unlock economy.
export const ALL_TAUNT_IDS = Object.freeze(Object.keys(TAUNT_DEFINITIONS));

/**
 * Resolves a taunt definition by its ID.
 * @param {string} id
 * @returns {object|null}
 */
export function getTauntById(id) {
  if (!id || typeof id !== 'string') return null;
  return TAUNT_DEFINITIONS[id] || null;
}

/**
 * Returns currently equipped taunts from inventory if available,
 * or the default 3 options.
 * @param {object} [inventoryInstance]
 * @returns {Array<object>}
 */
export function getEquippedTaunts(inventoryInstance = null) {
  if (inventoryInstance && typeof inventoryInstance.getEquippedTaunts === 'function') {
    const ids = inventoryInstance.getEquippedTaunts();
    if (Array.isArray(ids) && ids.length > 0) {
      return ids.map(id => getTauntById(id)).filter(Boolean);
    }
  }
  return DEFAULT_EQUIPPED_TAUNT_IDS.map(id => getTauntById(id)).filter(Boolean);
}
